import {Directive, ElementRef, HostListener, Input, forwardRef, OnInit, Renderer2, Inject, LOCALE_ID} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Directive({
    selector: 'input[type=text][appNumeric]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumericDirective),
            multi: true,
        },
    ],
    standalone: false
})
/**
 * Directive which controls value of an input to be number. It can be configured how many digits to be possible after comma.
 * Example of an input <input appNumeric decimals="2" type="text">.
 *
 * IMPORTANT THE TYPE OF THE "input" HAVE TO BE OF TYPE "text".
 *
 * Based on https://gist.github.com/ahmeti/5ca97ec41f6a48ef699ee6606560d1f7
 */
export class NumericDirective implements OnInit, ControlValueAccessor {

  @Input() decimals = 0;
  @Input() allowNegative = false;

  propagateChange;
  decimalSeparator: string;

  private specialKeys = [
    'Backspace', 'Tab', 'End', 'Home', 'ArrowLeft', 'ArrowRight', 'Delete',
  ];

  constructor(private el: ElementRef, private renderer: Renderer2, @Inject(LOCALE_ID) private locale: string) {

  }

  private static getDecimalSeparator(locale): string {
    return  1.1.toLocaleString(locale).substring(1, 2);
  }

  private static getRegex(decimals: number, allowNegative: boolean): string {
    return `^${allowNegative ? '-?' : ''}\\s*((\\d+((\\.|,)\\d{0,${decimals}})?)|((\\d*((\\.|,)\\d{1,${decimals}}))))\\s*$`;
  }

  @HostListener('input', [ '$event.target.value' ])
  input(value): void {
    const canonical = this.stringToNumber(value);
    this.render(value);
    this.propagateChange(canonical);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.specialKeys.indexOf(event.key) !== -1) {
      return;
    }
    // Do not use event.keycode this is deprecated.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
    const current: string = this.el.nativeElement.value;
    const next: string = current.concat(event.key);
    if (next && (!this.allowNegative || next !== '-') && !this.check(next)) {
      event.preventDefault();
    }
  }

  ngOnInit(): void {
    this.decimalSeparator = NumericDirective.getDecimalSeparator(this.locale);
  }

  writeValue(value: any): void {
    const canonical = this.stringToNumber(value);
    const formatted = this.numberToString(canonical);
    this.render(formatted);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {}

  numberToString(num: number): string {
    if (num) {
      return num.toString().replace(/\./gi, this.decimalSeparator);
    }
    return null;
  }

  stringToNumber(str: string): number {
    if (str) {
      return +str.toString().replace(/,/gi, '.');
    }
    return null;
  }

  private check(value: string): RegExpMatchArray {
    if (this.decimals <= 0) {
      return String(value).match(new RegExp(`^${this.allowNegative ? '-?' : ''}\\d+$`));
    }
    return String(value).match(new RegExp(NumericDirective.getRegex(this.decimals, this.allowNegative)));
  }

  private render(value): void {
    const element = this.el.nativeElement;
    this.renderer.setProperty(element, 'value', value);
  }
}
